import { Dialog, DialogTitle, DialogContent, DialogActions, Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PageButton from "src/components/PageButton";
import FormInput from "src/components/FormFields/FormFields";
import FormFileInput from "src/components/FormFields/FormFileInput";
import { useUploadDocumentMutation } from "src/reduxServer/services/consultations";
import { showToast } from "src/utils/toastfy";
import { useEffect } from "react";

interface Props {
    open: boolean;
    onClose: () => void;
    consultationId?: number;
    caseId?: number;
    refetchDocuments?: () => void
}

interface FormValues {
    consultation_id: number;
    case_id?: number;
    file: File | null;
}

const defaultValues: FormValues = {
    consultation_id: undefined,
    case_id: undefined,
    file: null
};

const UploadDocumentDialog = ({
    open,
    onClose,
    consultationId,
    caseId,
    refetchDocuments
}: Props) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset } = useForm<FormValues>({
        defaultValues: {
            consultation_id: consultationId || 0,
            case_id: caseId || 0,
            file: null
        }
    });

    useEffect(() => {
        if (open) {
            reset({
                consultation_id: consultationId || 0,
                case_id: caseId,
                file: null
            })
        }
    }, [open, consultationId, caseId, reset])

    const [uploadDocument, { isLoading }] = useUploadDocumentMutation();

    const onSubmit = async (data: FormValues) => {
        try {
            const formData = new FormData();
            if (data.consultation_id) {
                formData.append("consultation_id", String(data.consultation_id));
            }
            if (data.case_id) {
                formData.append("case_id", String(data.case_id));
            }

            if (data.file) {
                formData.append("file", data.file);
            }

            await uploadDocument(formData).unwrap();

            showToast("success", t("Document uploaded successfully"));
            refetchDocuments?.()
            reset(defaultValues);
            onClose();
        } catch (error: any) {
            console.error(error);
            showToast(
                "error",
                error?.data?.message || t("Failed to upload document")
            );
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t("Upload Document")}</DialogTitle>

            <DialogContent>
                <Grid container spacing={2} mt={1}>

                    {/* File Upload */}
                    <Grid item xs={12}>
                        <FormFileInput
                            name="file"
                            label={t("Upload File")}
                            control={control}
                            rules={{ required: t("File is required") }}
                            accept=".pdf,.doc,.docx,.jpg,.png"
                        />
                    </Grid>

                </Grid>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 2,
                    pb: 2,
                    display: "flex",
                    justifyContent: "flex-end"
                }}
            >
                <PageButton variant="outlined" onClick={onClose} sx={{
                    marginLeft:"10px"
                }}>
                    {t("Cancel")}
                </PageButton>

                <PageButton
                    variant="contained"
                    loading={isLoading}
                    onClick={handleSubmit(onSubmit)}
                >
                    {t("Upload")}
                </PageButton>
            </DialogActions>
        </Dialog>
    );
};

export default UploadDocumentDialog;